import React, { Ref } from "react";
import styled from "styled-components";
import {
  Elevation,
  FontSize,
  FontWeight,
  GlossyThemeProvider,
  Spacing,
  ZIndexes,
  background,
  border,
  content,
  cover,
  prettyScrollbars,
} from "@glossy";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import Button from "@ui-kit/button";
import Tooltip from "@ui-kit/tooltip";

const BLOCKS_HEIGHT = 72;

interface IProps {
  title: React.ReactNode;
  subheader?: React.ReactNode;
  children?: React.ReactNode;
  options?: React.ReactNode;
  hideFooter?: boolean;
  onValidate?: () => void;
  onClose: () => void;
  locale?: Locale;
  isValid?: boolean;
  validateTooltip?: string;
}

const Root = styled.div`
  ${cover()};
  position: fixed;
  z-index: ${ZIndexes.Panel};
  display: flex;
  flex-direction: column;
  background-color: ${background};
`;

const Block = styled.div<{ noPadding?: boolean }>`
  flex: ${BLOCKS_HEIGHT}px 0 0;
  padding: ${(props) => (props.noPadding ? "0" : "0 16px")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${background};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  z-index: ${ZIndexes.AboveContent};
  box-shadow: ${Elevation.Elevation_1};
  color: ${content};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  font-size: ${FontSize.ExtraLarge};
  font-weight: ${FontWeight.Bold};
  column-gap: ${Spacing.Micro};
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${Spacing.Medium};
`;

const Content = styled.div`
  flex: auto;
  overflow: auto;
  ${prettyScrollbars};
`;

const Footer = styled(Block)`
  z-index: ${ZIndexes.AboveContent};
  border-top: 1px solid ${border};
`;

const UiKitModalFullscreen = React.forwardRef(
  (
    {
      title,
      subheader,
      children,
      options,
      hideFooter,
      onValidate,
      onClose,
      locale,
      isValid = true,
      validateTooltip,
    }: IProps,
    ref: Ref<HTMLDivElement>
  ) => (
    <GlossyThemeProvider name="surface">
      <Root>
        <Header>
          <Block>
            <Title>
              <Button
                icon="cross"
                size="l"
                variant="tertiary"
                onClick={onClose}
              />
              {title}
            </Title>
            {options && <Options>{options}</Options>}
          </Block>
          {subheader && <Block noPadding>{subheader}</Block>}
        </Header>
        <Content ref={ref}>{children}</Content>
        {onValidate && !hideFooter && (
          <Footer>
            <Button
              data-testid="modal-button-cancel"
              variant="tertiary"
              size="m"
              text={tr(locale, "MODAL::CANCEL")}
              onClick={onClose}
            />
            <Tooltip tooltip={validateTooltip} disabled={isValid}>
              <div>
                <Button
                  data-testid="modal-button-validate"
                  variant="primary"
                  size="m"
                  text={tr(locale, "MODAL::OK")}
                  onClick={onValidate}
                  isDisabled={!isValid}
                />
              </div>
            </Tooltip>
          </Footer>
        )}
      </Root>
    </GlossyThemeProvider>
  )
);

export default UiKitModalFullscreen;
